import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Box,
  Alert,
  Tooltip,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Snackbar,
  useTheme,
  useMediaQuery,
  Stack,
  CardActions
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  BatteryChargingFull as ChargingIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useOrganization } from '../../context/OrganizationContext';
import { useAuth } from '../../context/authContext';
import AddBatteryDialog from '../../components/fleet/AddBatteryDialog';
import EditBatteryDialog from '../../components/fleet/EditBatteryDialog';
import { useToast } from '../../hooks/useToast';
import styles from '../../styles/BatteryInventory.module.css';

const BatteryInventory = () => {
  const { currentOrganization, userRole } = useOrganization();
  const { token } = useAuth();
  const { toast, showSuccess, showError, hideToast } = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [batteries, setBatteries] = useState([]);
  const [filteredBatteries, setFilteredBatteries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedBattery, setSelectedBattery] = useState(null);

  // Permission checks
  const canManageBatteries = ['owner', 'admin', 'manager'].includes(userRole?.toLowerCase());
  const canDeleteBatteries = ['owner', 'admin', 'manager'].includes(userRole?.toLowerCase());

  // Fetch batteries from backend
  useEffect(() => {
    if (currentOrganization?.organizationId) {
      fetchBatteries();
    }
  }, [currentOrganization]);

  const fetchBatteries = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/batteries`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch batteries');
      }

      const data = await response.json();
      const fetchedBatteries = data.batteries || [];
      setBatteries(fetchedBatteries);
      setFilteredBatteries(fetchedBatteries);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Search functionality
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredBatteries(batteries);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = batteries.filter(
        (battery) =>
          battery.manufacturer?.toLowerCase().includes(query) ||
          battery.model?.toLowerCase().includes(query) ||
          battery.serialNumber?.toLowerCase().includes(query) ||
          battery.nickname?.toLowerCase().includes(query) ||
          battery.status?.toLowerCase().includes(query)
      );
      setFilteredBatteries(filtered);
    }
  }, [searchQuery, batteries]);

  const handleAddBattery = async (batteryData) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/batteries`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(batteryData)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add battery');
      }

      await fetchBatteries();
      setAddDialogOpen(false);
      showSuccess('Battery added successfully!');
    } catch (err) {
      showError(`Failed to add battery: ${err.message}`);
      throw err;
    }
  };

  const handleEditBattery = async (batteryId, batteryData) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/batteries/${batteryId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(batteryData)
        }
      );


      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update battery');
      }

      const responseData = await response.json();

      await fetchBatteries();
      setEditDialogOpen(false);
      setSelectedBattery(null);
      showSuccess('Battery updated successfully!');
    } catch (err) {
      showError(`Failed to update battery: ${err.message}`);
      throw err;
    }
  };

  const handleDeleteBattery = async (batteryId) => {
    if (!window.confirm('Are you sure you want to delete this battery?')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization.organizationUuid}/batteries/${batteryId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete battery');
      }

      await fetchBatteries();
      showSuccess('Battery deleted successfully!');
    } catch (err) {
      showError(`Failed to delete battery: ${err.message}`);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'charging':
        return 'info';
      case 'storage':
        return 'default';
      case 'retired':
        return 'warning';
      case 'damaged':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatStatus = (status) => {
    if (!status) return '';
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const getHealthColor = (health) => {
    if (health >= 80) return 'success.main';
    if (health >= 60) return 'warning.main';
    return 'error.main';
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate statistics
  const maxBatteries = currentOrganization?.subscription?.limits?.maxBatteries || 0;
  const currentCount = batteries.length;
  const activeCount = batteries.filter(b => b.status === 'active').length;
  const chargingCount = batteries.filter(b => b.status === 'charging').length;
  const lowHealthCount = batteries.filter(b => b.healthPercentage < 70).length;
  const atLimit = maxBatteries > 0 && currentCount >= maxBatteries;

  if (loading) {
    return (
      <Container maxWidth="xl" className={styles.container}>
        <Typography>Loading battery inventory...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" className={styles.container}>
      {/* Header */}
      <Box className={styles.header}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
        >
          Battery Inventory
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your organization's battery fleet
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={isMobile ? 1.5 : 3} className={styles.statsGrid}>
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: isMobile ? '0.75rem' : '1.25rem', mb: isMobile ? 0.5 : 1 }}>
                Batteries
              </Typography>
              <Typography variant="h3" sx={{ fontSize: isMobile ? '1.5rem' : '3rem' }}>
                {currentCount} / {maxBatteries}
              </Typography>
              {atLimit && (
                <Alert severity="warning" sx={{ mt: 1, fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
                  Limit reached
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: isMobile ? '0.75rem' : '1.25rem', mb: isMobile ? 0.5 : 1 }}>
                Active
              </Typography>
              <Typography variant="h3" color="success.main" sx={{ fontSize: isMobile ? '1.5rem' : '3rem' }}>
                {activeCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: isMobile ? '0.75rem' : '1.25rem', mb: isMobile ? 0.5 : 1 }}>
                Charging
              </Typography>
              <Typography variant="h3" color="info.main" sx={{ fontSize: isMobile ? '1.5rem' : '3rem' }}>
                {chargingCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: isMobile ? '0.75rem' : '1.25rem', mb: isMobile ? 0.5 : 1 }}>
                Low Health
              </Typography>
              <Typography variant="h3" color="error.main" sx={{ fontSize: isMobile ? '1.5rem' : '3rem' }}>
                {lowHealthCount}
              </Typography>
              {lowHealthCount > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}>
                  {'<'}70% health
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Add Button */}
      <Box
        className={styles.searchBar}
        sx={{
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 2 : 0
        }}
      >
        <TextField
          placeholder="Search batteries by manufacturer, model, serial number..."
          variant="outlined"
          fullWidth={isMobile}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
        {canManageBatteries && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            disabled={atLimit}
            fullWidth={isMobile}
          >
            Add Battery
          </Button>
        )}
      </Box>

      {/* Battery Table/Cards */}
      {isMobile ? (
        // Mobile Card View
        <Box sx={{ mt: 3 }}>
          {filteredBatteries.length === 0 ? (
            <Card>
              <CardContent>
                <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
                  {searchQuery
                    ? 'No batteries found matching your search'
                    : 'No batteries in your fleet yet. Add your first battery to get started!'}
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Stack spacing={1.5}>
              {filteredBatteries.map((battery) => (
                <Card key={battery.id} variant="outlined">
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    {/* Compact Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                          {battery.nickname || battery.model}
                        </Typography>
                        {battery.nickname && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {battery.model}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        label={formatStatus(battery.status)}
                        color={getStatusColor(battery.status)}
                        size="small"
                        icon={battery.status === 'charging' ? <ChargingIcon /> : undefined}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>

                    {/* Compact Details - 3 columns */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 1 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          Capacity
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                          {battery.capacity} mAh
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          Voltage
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                          {battery.voltage} V
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          Cycles
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                            {battery.cycleCount}
                          </Typography>
                          {battery.cycleCount > 300 && (
                            <WarningIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                          )}
                        </Box>
                      </Box>
                    </Box>

                    {/* Compact Health Bar */}
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          Health
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem' }}>
                          {battery.healthPercentage}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={battery.healthPercentage}
                        sx={{
                          height: 6,
                          borderRadius: 1,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: getHealthColor(battery.healthPercentage)
                          }
                        }}
                      />
                    </Box>
                  </CardContent>

                  {/* Actions */}
                  {canManageBatteries && (
                    <CardActions sx={{ p: 1, pt: 0, gap: 0.5, justifyContent: 'flex-end' }}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedBattery(battery);
                          setEditDialogOpen(true);
                        }}
                        sx={{ padding: '4px' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      {canDeleteBatteries && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteBattery(battery.id)}
                          sx={{ padding: '4px' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </CardActions>
                  )}
                </Card>
              ))}
            </Stack>
          )}
        </Box>
      ) : (
        // Desktop Table View
        <TableContainer component={Paper} className={styles.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nickname / Model</TableCell>
                <TableCell>Manufacturer</TableCell>
                <TableCell>Serial Number</TableCell>
                <TableCell>Capacity</TableCell>
                <TableCell>Voltage</TableCell>
                <TableCell>Health</TableCell>
                <TableCell>Cycle Count</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Charged</TableCell>
                {canManageBatteries && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBatteries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManageBatteries ? 10 : 9} align="center">
                    <Typography variant="body1" color="text.secondary" sx={{ py: 4 }}>
                      {searchQuery
                        ? 'No batteries found matching your search'
                        : 'No batteries in your fleet yet. Add your first battery to get started!'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredBatteries.map((battery) => (
                  <TableRow key={battery.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body1" fontWeight={600}>
                          {battery.nickname || battery.model}
                        </Typography>
                        {battery.nickname && (
                          <Typography variant="caption" color="text.secondary">
                            {battery.model}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{battery.manufacturer}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {battery.serialNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>{battery.capacity} mAh</TableCell>
                    <TableCell>{battery.voltage} V</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: '100%', minWidth: 80 }}>
                          <LinearProgress
                            variant="determinate"
                            value={battery.healthPercentage}
                            sx={{
                              height: 8,
                              borderRadius: 1,
                              backgroundColor: '#e0e0e0',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: getHealthColor(battery.healthPercentage)
                              }
                            }}
                          />
                        </Box>
                        <Typography variant="body2" sx={{ minWidth: 45 }}>
                          {battery.healthPercentage}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {battery.cycleCount}
                      {battery.cycleCount > 300 && (
                        <Tooltip title="High cycle count - consider replacement">
                          <WarningIcon sx={{ fontSize: 16, color: 'warning.main', ml: 0.5 }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatStatus(battery.status)}
                        color={getStatusColor(battery.status)}
                        size="small"
                        icon={battery.status === 'charging' ? <ChargingIcon /> : undefined}
                      />
                    </TableCell>
                    <TableCell>{formatDate(battery.lastChargedDate)}</TableCell>
                    {canManageBatteries && (
                      <TableCell align="right">
                        <Tooltip title="Edit battery">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedBattery(battery);
                              setEditDialogOpen(true);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canDeleteBatteries && (
                          <Tooltip title="Delete battery">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteBattery(battery.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Battery Dialog */}
      {addDialogOpen && (
        <AddBatteryDialog
          open={addDialogOpen}
          onClose={() => setAddDialogOpen(false)}
          onAdd={handleAddBattery}
        />
      )}

      {/* Edit Battery Dialog */}
      {editDialogOpen && selectedBattery && (
        <EditBatteryDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedBattery(null);
          }}
          onEdit={handleEditBattery}
          battery={selectedBattery}
        />
      )}

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={hideToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={hideToast} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default BatteryInventory;
